import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { cloneDeep } from 'lodash'
import * as Constants from '../../../../config/Constants'
import * as Utility from '../../../shared/Utility'
import { differenceInMinutes } from 'date-fns'
import { getCustomerApplication } from '../../../shared/auth/license/LicenseChecker'
import { saveSid, validateSid } from '../../../shared/SidCertsHandler'
import * as actions from '../../../../actions'
import getText from '../../../shared/i18n/labels'

// FORM
import { AutoCompileCustom } from '../../../shared/form/AutoCompileCustomB5'
import { DateField } from '../../../shared/form/DateFieldB5'
import { BwmInput } from '../../../shared/form/BwmInputB5'
import { BwmSelect } from '../../../shared/form/BwmSelectB5'
import { ProvinceField } from '../../../shared/form/ProvinceFieldB5'
import ErrorListAlert from '../../../shared/form/ErrorListAlert'
import { Formik, Form, useFormikContext } from 'formik'
import * as Yup from 'yup'

import SidContactFormContainer from '../../../shared/form/certificati_firma_comunicazioni_crs_cora/SidContactFormContainer'
import { PopupError } from '../../../shared/popups/PopupError'
import { PopupConfirm, titleColors } from '../../../shared/popups/PopupConfirm'
import CodiceFiscale from 'codice-fiscale-js'

/* eslint-disable */
const defaultSize = (size = null) => ({
  leftSide: Utility.getSize(size, { default: '58', lg: '54', md: '54' }),
  rightSide: Utility.getSize(size, { default: '42', lg: '46', md: '46' }),

  // left
  personType: Utility.getSize(size, { default: '90' }),
  fiscalCode: Constants.fiscalCodeSize(size),
  companyName: Utility.getSize(size, { default: '60', additionalClass: 'flex-grow-1' }),
  location: Utility.getSize(size, { default: '45', lg: '56', md: '44' }),
  province: Constants.provinceSize(size),
  firstName: Utility.getSize(size, { default: '28', additionalClass: 'flex-grow-1' }),
  lastName: Utility.getSize(size, { default: '28', additionalClass: 'flex-grow-1' }),
  gender: Constants.genderSize(size),
  birthDate: Constants.dateSize(size),
  // SID FORM
  // right
  sidNumber: Constants.sidSize(size),
  password: Utility.getSize(size, { default: '42', additionalClass: 'flex-grow-1' }),
  expirationDate: Utility.getSize(size, { default: '36', additionalClass: 'flex-grow-1' }),
  email: Utility.getSize(size, { default: '55' }),
  flagPec: Utility.getSize(size, { default: '45' })
})
/* eslint-enable */

const getSize = () => {
  if (window.innerWidth < Constants.BREAKPOINT_MD) return defaultSize('md')
  if (window.innerWidth <= Constants.BREAKPOINT_LG) return defaultSize('lg')
  return defaultSize()
}

export const CoraSubjectForm = (props) => {
  const [showErrorAlert, setShowErrorAlert] = useState(false)
  const [initialValues, setInitialValues] = useState({})
  const [touched, setTouched] = useState(false)
  const [loading, setLoading] = useState(false)

  const lang = useSelector((state) => state.language.language)
  const labels = getText(lang)

  const [subject, setSubject] = useState({})

  const [coraSubjectSid, setCoraSubjectSid] = useState({
    id: null,
    certificate1: '',
    certificate2: '',
    certificate3: '',
    password: '',
    email: '',
    sidNumber: '',
    flagPec: false,
    expirationDate: null
  })

  const [initialCoraSubjectSid, setInitialCoraSubjectSid] = useState({
    id: null,
    certificate1: '',
    certificate2: '',
    certificate3: '',
    password: '',
    email: '',
    sidNumber: '',
    flagPec: false,
    expirationDate: null
  })

  const [errorsSid, setErrorsSid] = useState({})

  useEffect(() => {
    retrieveSubject(props.subjectId)
  }, [props.subjectId])

  useEffect(() => {
    if (props.setLoading) props.setLoading(loading)
  }, [loading])

  const validationSchema = Yup.object().shape({
    personType: Yup.string().required(labels.REQUIRED_FIELD),
    //persone giuridiche: denominazione obbligatoria
    companyName: Yup.string().when('personType', {
      is: (val) => val === Constants.PNF,
      then: (schema) => schema.required(labels.REQUIRED_FIELD),
      otherwise: (schema) => schema.notRequired()
    }),
    //località e provincia sempre obbligatori
    location: Yup.string().required(labels.REQUIRED_FIELD),
    province: Yup.string().required(labels.REQUIRED_FIELD),

    //nome, cognome, data di nascitam genere obbligatori per persona fisica
    firstName: Yup.string().when('personType', {
      is: (val) => val === Constants.PF,
      then: (schema) => schema.required(labels.REQUIRED_FIELD),
      otherwise: (schema) => schema.notRequired()
    }),
    lastName: Yup.string().when('personType', {
      is: (val) => val === Constants.PF,
      then: (schema) => schema.required(labels.REQUIRED_FIELD),
      otherwise: (schema) => schema.notRequired()
    }),
    birthDate: Yup.date(labels.INVALID_DATE).when('personType', {
      is: (val) => val === Constants.PF,
      then: (schema) =>
        schema
          .required(labels.REQUIRED_FIELD)
          .test(
            'is-valid-date',
            labels.INVALID_DATE,
            (value) => !value || Utility.isValidDate(new Date(value))
          ),
      otherwise: (schema) => schema.notRequired()
    }),
    gender: Yup.string().when('personType', {
      is: (val) => val === Constants.PF,
      then: (schema) => schema.required(labels.REQUIRED_FIELD),
      otherwise: (schema) => schema.notRequired()
    }),
    //CF obbligatorio
    fiscalCode: Yup.string().required(labels.REQUIRED_FIELD)
  })

  const retrieveSubject = (id) => {
    setLoading(true)
    setErrorsSid({})
    if (id) {
      actions
        .getCoraSubjectDetail(id)
        .then((response) => {
          setSubject(response.data)
          setNewInitialValues(response.data)
          setLoading(false)
        })
        .catch(() => setLoading(false))
    } else {
      setSubject({})
      setNewInitialValues(undefined)
      setLoading(false)
    }
  }

  const setNewInitialValues = (subjectItem) => {
    setTouched(false)
    if (subjectItem?.id) {
      setCoraSubjectSid(subjectItem.coraSubjectSid)
      setInitialCoraSubjectSid(subjectItem.coraSubjectSid)
    } else {
      setCoraSubjectSid({
        id: null,
        certificate1: '',
        certificate2: '',
        certificate3: '',
        password: '',
        email: '',
        sidNumber: '',
        flagPec: false,
        expirationDate: null
      })
      setInitialCoraSubjectSid({
        id: null,
        certificate1: '',
        certificate2: '',
        certificate3: '',
        password: '',
        email: '',
        sidNumber: '',
        flagPec: false,
        expirationDate: null
      })
    }
    setInitialValues({
      id: subjectItem?.id || null,
      personType: subjectItem?.personType || Constants.PNF,
      fiscalCode: subjectItem?.fiscalCode,
      companyName: subjectItem?.companyName,
      location: subjectItem?.location,
      province: subjectItem?.province,
      firstName: subjectItem?.firstName,
      lastName: subjectItem?.lastName,
      gender: subjectItem?.gender || 'FEMALE',
      birthDate: subjectItem?.birthDate,
      customer: subjectItem?.customer,
      archived: subjectItem?.archived,
      lastArchivedDate: subjectItem?.lastArchivedDate
    })
  }

  const close = (retrieve = false) => {
    if (retrieve) retrieveSubject(props.subjectId)
    setTouched(false)
    setErrorsSid({})
  }

  const formErrorsTabMapping = [
    {
      errorKey: 'fiscalCode',
      errorLabel: labels.FISCAL_CODE
    },
    {
      errorKey: 'firstName',
      errorLabel: labels.FIRST_NAME
    },
    {
      errorKey: 'lastName',
      errorLabel: labels.LAST_NAME
    },
    {
      errorKey: 'companyName',
      errorLabel: labels.LEGAL_NAME
    },
    {
      errorKey: 'location',
      errorLabel: labels.LEGAL_ADDRESS
    },
    {
      errorKey: 'province',
      errorLabel: labels.PROVINCE
    },
    {
      errorKey: 'gender',
      errorLabel: labels.GENDER
    },
    {
      errorKey: 'birthDate',
      errorLabel: labels.BIRTH_DATE
    },
    {
      errorKey: 'errorSidNumber',
      errorLabel: labels.SID_NUMBER
    },
    {
      errorKey: 'errorPassword',
      errorLabel: labels.PROTECTION_PASSWORD
    },
    {
      errorKey: 'errorInSubject',
      errorLabel: labels.SUBJECT
    },
    {
      errorKey: 'errorInConfigSid',
      errorLabel: labels.SID_CONFIGURATION
    },
    {
      errorKey: 'errorEmail',
      errorLabel: labels.EMAIL_ADDRESS
    }
  ]

  const getError = (errors, key, removeOldErrors) => {
    if (errorsSid[key]?.isValid === false) {
      errors[key] = errorsSid[key].msg
    } else if (removeOldErrors) {
      delete errors[key]
    }
    return errors
  }

  const getTotalErrors = (errors = {}, removeOldErrors = false) => {
    errors = getError(errors, 'errorCertificates', removeOldErrors)
    errors = getError(errors, 'errorPassword', removeOldErrors)
    errors = getError(errors, 'errorSidNumber', removeOldErrors)
    errors = getError(errors, 'errorEmail', removeOldErrors)
    errors = getError(errors, 'errorInConfigSid', removeOldErrors)
    return errors
  }

  const getErrors = (errors, personType) => {
    if (personType === Constants.PF) formErrorsTabMapping[4].errorLabel = labels.BIRTH_PLACE
    else formErrorsTabMapping[4].errorLabel = labels.LEGAL_ADDRESS
    const totalErrors = getTotalErrors(errors, true)
    return Utility.extractErrors(totalErrors, formErrorsTabMapping)
  }

  return (
    <div className="border-top">
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}>
        {({ values, errors }) => (
          <Form className={props.formOnBottom ? 'p-2 form-on-bottom mb-0' : ''}>
            {showErrorAlert && touched && (
              <div className={props.formOnBottom ? 'cora-form-on-bottom-error' : ''}>
                <ErrorListAlert
                  errors={getErrors(errors, values.personType)}
                  hide={() => setShowErrorAlert(false)}
                />
              </div>
            )}
            <FormBody
              {...props}
              initialValues={initialValues}
              loading={loading}
              touched={touched}
              subject={subject}
              errorsSid={errorsSid}
              coraSubjectSid={coraSubjectSid}
              initialCoraSubjectSid={initialCoraSubjectSid}
              retrieveSubject={retrieveSubject}
              setTouched={setTouched}
              setSubject={setSubject}
              setInitialCoraSubjectSid={setInitialCoraSubjectSid}
              setLoading={setLoading}
              setCoraSubjectSid={setCoraSubjectSid}
              setErrorsSid={setErrorsSid}
              setShowErrorAlert={setShowErrorAlert}
              getTotalErrors={getTotalErrors}
              close={close}
            />
          </Form>
        )}
      </Formik>
    </div>
  )
}

const FormBody = (props) => {
  const { values, errors, setValues, validateForm } = useFormikContext()

  const [sizesClass, setSizesClass] = useState(getSize())

  const lang = useSelector((state) => state.language.language)
  const labels = getText(lang)

  const nameApplication = Constants.APPLICATION_CORA
  const id = props.subjectId

  const [certificateFiles, setCertificateFiles] = useState()
  const [isCertSidOpened, setIsCertSidOpened] = useState(false)

  useEffect(() => {
    setValues(props.initialValues)
  }, [props.initialValues])

  useEffect(() => {
    setSizesClass(getSize())
  }, [window.innerWidth])

  const handlePersonTypeClick = (val) => {
    if (props.disabled || values.archived) return
    const form = Object.assign({}, values)
    form.personType = val
    setValues(form)
  }

  const checkSid = async (sidNumber) => {
    const _coraSubjectSid = cloneDeep(props.coraSubjectSid)
    try {
      if (sidNumber === props.initialCoraSubjectSid?.sidNumber || !sidNumber) return true
      const response = await actions.checkCoraSid(sidNumber)
      if (!response) {
        PopupError({
          text: `<b>${labels.SID_NUMBER}</b>: ${sidNumber}<br> ${labels.DETECTED_FOR_MULTIPLE_SUBJECTS_RECORDING_IMPOSSIBLE}`
        })
        props.setCoraSubjectSid({
          ..._coraSubjectSid,
          sidNumber: props.initialCoraSubjectSid?.sidNumber
        })
      }
      return response
    } catch {
      PopupError({
        text: `<b>${labels.SID_NUMBER}</b>: ${sidNumber}<br> ${labels.DETECTED_FOR_MULTIPLE_SUBJECTS_RECORDING_IMPOSSIBLE}`
      })
      props.setCoraSubjectSid({
        ..._coraSubjectSid,
        sidNumber: props.initialCoraSubjectSid?.sidNumber
      })
      return false
    }
  }

  const handleChangeSid = (e, errorKey, errorMessage) => {
    let value = e.target.value
    let name = e.target.name
    let newCoraSubjectSid = cloneDeep(props.coraSubjectSid)
    if (!newCoraSubjectSid) newCoraSubjectSid = {}
    const error = {}
    error[errorKey] = {}
    if (Utility.isEmpty(value)) {
      error[errorKey].isValid = false
      error[errorKey].msg = errorMessage
    } else {
      error[errorKey].isValid = true
      error[errorKey].msg = null
    }
    props.setErrorsSid({ ...props.errorsSid, errorInConfigSid: '', ...error })
    if (name === 'password') newCoraSubjectSid.password = value.toUpperCase().trim()
    if (name === 'sidNumber') newCoraSubjectSid.sidNumber = value.toUpperCase().trim()
    if (name === 'email') newCoraSubjectSid.email = value ? value.toUpperCase().trim() : value
    props.setCoraSubjectSid(newCoraSubjectSid)
  }

  const handleChangeSidValues = (newValues) => {
    props.setCoraSubjectSid({ ...props.coraSubjectSid, ...newValues })
  }

  const newFiscalCode = (val, form) => {
    if (val?.length >= 16 || form.personType === Constants.PF) {
      try {
        const cf = new CodiceFiscale(val).toJSON()
        if (cf) {
          form.birthDate = form.birthDate || cf?.birthday ? new Date(cf?.birthday) : null
          form.gender = cf?.gender === 'M' ? 'MALE' : 'FEMALE'
          form.location = cf?.birthplaceProvincia !== 'EE' ? cf?.birthplace.toUpperCase() : ''
          form.province = cf?.birthplaceProvincia || ''
        }
      } catch (e) {
        console.log(e)
      }
    }
    setValues(form)
  }

  const handleFiscalCode = async (fiscalCode) => {
    const form = Object.assign({}, values)
    if (fiscalCode) {
      try {
        const baseRegistry = await actions.getBaseRegistryByFiscalCode(fiscalCode)
        if (baseRegistry) {
          form.companyName = baseRegistry?.companyName || ''
          form.location = baseRegistry?.location?.location || ''
          form.province = baseRegistry?.location?.province || ''
          form.lastName = baseRegistry?.lastName || ''
          form.firstName = baseRegistry?.firstName || ''
          form.birthDate = baseRegistry?.birthDate ? new Date(baseRegistry?.birthDate) : null
          form.gender = baseRegistry?.gender
          setValues(form)
        } else {
          newFiscalCode(fiscalCode, form)
        }
      } catch {
        newFiscalCode(fiscalCode, form)
      }
      try {
        if (!values.id) {
          const _coraSubjectSid = await actions.getCoraSubjectSidByFiscalCode(fiscalCode, 'cora')
          if (_coraSubjectSid?.id) props.setCoraSubjectSid(_coraSubjectSid)
        }
      } catch (e) {
        console.error(e)
      }
    }
  }

  const handleInputAutocompile = (newLocation) => {
    if (!newLocation || newLocation === '') {
      const form = Object.assign({}, values)
      form.location = newLocation
      setValues(form)
    }
    newLocation = newLocation.toUpperCase()
    actions.getLocation(newLocation).then(
      (loc) => {
        const form = Object.assign({}, values)
        form.location = newLocation
        if (loc) form.province = loc.province
        setValues(form)
      },
      () => {
        const form = Object.assign({}, values)
        form.location = ''
        form.province = ''
        setValues(form)
      }
    )
  }

  const formValidation = () => {
    if (!isCertSidOpened) {
      return {
        errorSidNumber: { isValid: true, msg: '' },
        errorEmail: { isValid: true, msg: '' }
      }
    }
    return configurationSidValidation()
  }

  const updateAll = (coraSubjectSid, files, afterSubmit = () => {}) => {
    saveSid(
      files,
      coraSubjectSid || 'cora',
      values.fiscalCode,
      'cora',
      (res) => addOrUpdateCoraSubject(res, { ...values }, afterSubmit),
      () => {
        PopupError({ text: labels.GENERIC_ERROR })
        resetFields()
      }
    )
  }

  const onSuccess = (subject, callback = () => {}, reload = false) => {
    props.close()
    callback(subject, reload)
  }

  const addOrUpdateCoraSubject = (sid, values, afterSubmit = () => {}) => {
    let newSubject = { ...values }
    if (newSubject.personType === Constants.PF) {
      newSubject.companyName = null
    } else {
      newSubject.gender = null
      newSubject.firstName = null
      newSubject.lastName = null
      newSubject.birthDate = null
    }

    newSubject.coraSubjectSid = sid
    props.setInitialCoraSubjectSid(props.coraSubjectSid)
    props.setSubject(newSubject)
    newSubject.gender = Utility.getGender(newSubject.gender)
    props.addOrUpdateCoraSubject(newSubject, props.subject?.fiscalCode)
    afterSubmit(newSubject)
  }

  const resetFields = () => {
    props.setCoraSubjectSid({})
  }

  const configurationSidValidation = () => {
    return validateSid(
      props.coraSubjectSid?.sidNumber,
      props.coraSubjectSid?.email,
      props.errorsSid.errorSidNumber,
      props.errorsSid.errorEmail,
      labels
    )
  }

  const handleFormSubmit = async (newCoraSubjectSid, newFiles, afterSubmit = () => {}) => {
    if (props.disabled) return
    const coraSubjectSid = cloneDeep(newCoraSubjectSid || props.coraSubjectSid)
    const files = cloneDeep(newFiles || certificateFiles)
    props.setCoraSubjectSid(coraSubjectSid)
    const form = Object.assign({}, values)
    const errors = await validateForm(form) // Validazione dei valori
    setValues(form)
    props.setTouched(true)
    const { errorSidNumber, errorEmail } = formValidation()
    const totalErrors = props.getTotalErrors({
      ...errors,
      errorSidNumber: errorSidNumber?.msg,
      errorEmail: errorEmail?.msg
    })

    const thereAreErrors =
      Object.keys(totalErrors).length > 0 &&
      Object.values(totalErrors).filter((e) => !!e)?.length > 0
    props.setErrorsSid({ ...props.errorsSid, errorSidNumber, errorEmail })
    props.setLoading(true)
    const response = await checkSid(coraSubjectSid?.sidNumber)
    props.setLoading(false)
    if (!response) return
    if (thereAreErrors) {
      props.setShowErrorAlert(true)
    } else {
      updateAll(coraSubjectSid, files, afterSubmit)
      props.setShowErrorAlert(false)
    }
  }

  const renderPopupLicenzaArchiviazione = () => {
    PopupError({
      text:
        lang === 'it'
          ? `Questa funzionalità permette di archiviare il soggetto, mettendolo in sola lettura e permettendo l'inserimento di nuovi soggetti
                </br>
                <button class="btn btn-danger mt-5" 
                onclick="location.href='${Constants.CART_LINK(Constants.getEcommerceProductId(Constants.APPLICATION_ARCHIVIAZIONE))}'" type="button">
                ACQUISTA ADESSO
                </button>`
          : `This feature allows you to archive the subject, putting it in read-only mode and allowing the insertion of new subjects
                </br>
                <button class="btn btn-danger mt-5" 
                onclick="location.href='${Constants.CART_LINK(Constants.getEcommerceProductId(Constants.APPLICATION_ARCHIVIAZIONE))}'" type="button">
                BUY NOW
                </button>`
    })
  }

  const handleArchive = () => {
    let txt = ''
    const isExpired = actions.isApplicationExpired(
      getCustomerApplication(
        values.customer?.customerApplications,
        Constants.APPLICATION_ARCHIVIAZIONE
      )
    )

    if (isExpired) {
      renderPopupLicenzaArchiviazione()
      return
    }

    if (values.archived) {
      txt = () => labels.RESTORE_ARCHIVE_SUBJECT_CONFIRM
    } else {
      txt = () => {
        return labels.ARCHIVE_SUBJECT_MODAL_CONFIRM
      }
    }
    PopupConfirm({
      titleColor: titleColors.ALERT,
      component: txt,
      handleClickConfirm: () => {
        actions.archiveCoraSubject(id).then(
          (res) => {
            Utility.notifySuccess(labels.OPERATION_COMPLETED_SUCCESSFULLY)
            props.retrieveSubject(props.subjectId)
            props.checkLicense()
            onSuccess(res, props.onSaveClick, true)
          },
          (err) => {
            if (err.status === 511) {
              PopupError({
                text: labels.CANNOT_RESTORE_SUBJECT_LIMIT_REACHED
              })
            } else {
              PopupError({ text: err.title || labels.GENERIC_ERROR })
            }
          }
        )
      }
    })
  }

  const diff = differenceInMinutes(new Date(), new Date(values?.lastArchivedDate))

  return (
    <div className="row row-detail d-flex">
      <div className={`${sizesClass.leftSide} pe-3`}>
        <div className="configurazioneSID pt-2">
          {/* TIPO DI PERSONA */}
          <div id={`anagrafica-${id || 0}`}>
            <div className="d-flex justify-content-between">
              <div className={`mb-3 pt-2 ps-0`}>
                <div className="input-group">
                  <div className="inputRadio me-5">
                    <label
                      disabled={props.disabled || values.archived}
                      className={`form-check-label d-flex align-items-center ${props.disabled ? 'pointer-events-none' : ''}`}
                      htmlFor={`pType1${values?.id}`}
                      onClick={() => handlePersonTypeClick(Constants.PNF)}>
                      <input
                        disabled={props.disabled || values.archived}
                        className="form-check-input pe-1 me-2 mt-0"
                        style={{ width: '1.2em', height: '1.2em', boxShadow: 'none' }}
                        type="radio"
                        checked={values.personType === Constants.PNF}
                        onChange={() => {}}
                      />
                      {labels.LEGAL_PERSON}
                    </label>
                  </div>
                  <div className="inputRadio">
                    <label
                      disabled={props.disabled || values.archived}
                      className={`form-check-label d-flex align-items-center ${props.disabled ? 'pointer-events-none' : ''}`}
                      htmlFor={`pType2${values?.id}`}
                      onClick={() => handlePersonTypeClick(Constants.PF)}>
                      <input
                        disabled={props.disabled || values.archived}
                        className="form-check-input pe-1 me-2 mt-0"
                        style={{ width: '1.2em', height: '1.2em', boxShadow: 'none' }}
                        type="radio"
                        checked={values.personType === Constants.PF}
                        onChange={() => {}}
                      />
                      {labels.PHYSICAL_PERSON}
                    </label>
                  </div>
                </div>
              </div>
              {values.archived && (
                <div className="tag-orange tag-orange-form py-0">{labels.ARCHIVED_SUBJECT}</div>
              )}
            </div>

            <div className="row">
              {values.personType === Constants.PNF ? (
                <>
                  {/* CODICE FISCALE */}
                  <div className={`${sizesClass.fiscalCode} pe-2 mb-2`}>
                    <BwmInput
                      disabled={props.disabled || values.archived}
                      name="fiscalCode"
                      label={`${labels.FISCAL_CODE}*`}
                      className="form-control"
                      maxLength="16"
                      value={values.fiscalCode}
                      error={errors.fiscalCode}
                      touched={props.touched}
                      onBlur={(e) => handleFiscalCode(e.target.value)}
                      onChange={(e) =>
                        Utility.handleChangeValue(
                          e.target.value,
                          'fiscalCode',
                          values,
                          setValues,
                          true,
                          true
                        )
                      }
                    />
                  </div>
                  {/* RAGIONE SOCIALE */}
                  <div className={`${sizesClass.companyName} mb-2`}>
                    <BwmInput
                      disabled={props.disabled || values.archived}
                      label={`${labels.LEGAL_NAME}*`}
                      className="form-control font-weight-bold font-size-big"
                      maxLength="60"
                      value={values.companyName}
                      error={errors.companyName}
                      touched={props.touched}
                      onChange={(e) =>
                        Utility.handleChangeValue(
                          e.target.value,
                          'companyName',
                          values,
                          setValues,
                          true
                        )
                      }
                    />
                  </div>
                  {/* LOCALITÀ */}
                  <div className={`${sizesClass.location} pe-2 mb-2`}>
                    <AutoCompileCustom
                      disabled={props.disabled || values.archived}
                      label={
                        values.personType === Constants.PNF
                          ? `${labels.LEGAL_ADDRESS}*`
                          : `${labels.BIRTH_PLACE}*`
                      }
                      id="location"
                      filter={props.locations}
                      value={values.location}
                      error={errors.location}
                      touched={props.touched}
                      handleInputAutocompile={(e) => handleInputAutocompile(e)}
                    />
                  </div>
                  {/* PROVINCIA */}
                  <div className={`${sizesClass.province} pe-2 mb-2`}>
                    <ProvinceField
                      disabled={props.disabled || values.archived}
                      placeholder={`${labels.EE_FOR_FOREIGN_COUNTRY}`}
                      label={`${labels.PROVINCE}*`}
                      province={values.province}
                      error={errors.province}
                      touched={props.touched}
                      onChange={(e) =>
                        Utility.handleChangeValue(
                          e.target.value,
                          'province',
                          values,
                          setValues,
                          true,
                          true
                        )
                      }
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* CODICE FISCALE */}
                  <div className={`${sizesClass.fiscalCode} pe-2 mb-2`}>
                    <BwmInput
                      disabled={props.disabled || values.archived}
                      name="fiscalCode"
                      label={`${labels.FISCAL_CODE}*`}
                      className="form-control"
                      value={values.fiscalCode}
                      error={errors.fiscalCode}
                      touched={props.touched}
                      maxLength="16"
                      onBlur={(e) => handleFiscalCode(e.target.value)}
                      onChange={(e) =>
                        Utility.handleChangeValue(
                          e.target.value,
                          'fiscalCode',
                          values,
                          setValues,
                          true,
                          true
                        )
                      }
                    />
                  </div>
                  {/* NOME */}
                  <div className={`${sizesClass.firstName} pe-2`}>
                    <BwmInput
                      disabled={props.disabled || values.archived}
                      name="firstName"
                      maxLength="25"
                      label={`${labels.FIRST_NAME}*`}
                      type="text"
                      className="form-control font-weight-bold font-size-big"
                      value={values.firstName}
                      error={errors.firstName}
                      touched={props.touched}
                      onChange={(e) =>
                        Utility.handleChangeValue(
                          e.target.value,
                          'firstName',
                          values,
                          setValues,
                          true
                        )
                      }
                    />
                  </div>
                  {/* COGNOME */}
                  <div className={`${sizesClass.lastName} mb-2 pe-2`}>
                    <BwmInput
                      disabled={props.disabled || values.archived}
                      maxLength="25"
                      label={`${labels.LAST_NAME}*`}
                      type="text"
                      className="form-control font-weight-bold font-size-big"
                      value={values.lastName}
                      error={errors.lastName}
                      touched={props.touched}
                      onChange={(e) =>
                        Utility.handleChangeValue(
                          e.target.value,
                          'lastName',
                          values,
                          setValues,
                          true
                        )
                      }
                    />
                  </div>
                  {/* SESSO */}
                  <div className={`${sizesClass.gender} pe-2 mb-2`}>
                    <BwmSelect
                      disabled={props.disabled || values.archived}
                      options={Constants.genderList || []}
                      name="gender"
                      label={`${labels.GENDER}*`}
                      optionValue="id"
                      optionLabel="description"
                      isClearable={false}
                      isSearchable={false}
                      value={values.gender}
                      error={errors.gender}
                      touched={props.touched}
                      onChange={(value) =>
                        Utility.handleChangeValue(value?.id, 'gender', values, setValues)
                      }
                    />
                  </div>
                  {/* DATA DI NASCITA */}
                  <div className={`${sizesClass.birthDate} pe-2 mb-2`}>
                    <DateField
                      disabled={props.disabled || values.archived}
                      label={`${labels.BIRTH_DATE}*`}
                      customOptions={{ maxDate: new Date() }}
                      date={values.birthDate}
                      error={errors.birthDate}
                      touched={props.touched}
                      onChange={(value) =>
                        Utility.handleChangeValue(value, 'birthDate', values, setValues)
                      }
                    />
                  </div>
                  {/* LOCALITÀ O LUOGO DI NASCITA */}
                  <div className={`${sizesClass.location} pe-2 mb-2`}>
                    <AutoCompileCustom
                      disabled={props.disabled || values.archived}
                      label={
                        values.personType === Constants.PNF
                          ? `${labels.LEGAL_ADDRESS}*`
                          : `${labels.BIRTH_PLACE}*`
                      }
                      id="location"
                      filter={props.locations}
                      value={values.location}
                      error={errors.location}
                      touched={props.touched}
                      handleInputAutocompile={(e) => handleInputAutocompile(e)}
                    />
                  </div>
                  {/* PROVINCIA */}
                  <div className={`${sizesClass.province} mb-2`}>
                    <ProvinceField
                      disabled={props.disabled || values.archived}
                      maxLength="2"
                      placeholder={labels.OUTSIDEITALY}
                      label={`${labels.PROVINCE}*`}
                      province={values.province}
                      error={errors.province}
                      touched={props.touched}
                      onChange={(e) =>
                        Utility.handleChangeValue(
                          e.target.value,
                          'province',
                          values,
                          setValues,
                          true,
                          true
                        )
                      }
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={sizesClass.rightSide}>
        <div className="configurazioneSID pt-2 pb-1">
          <SidContactFormContainer
            disabled={props.disabled}
            archived={values.archived}
            id={props.subjectId || 0}
            sizes={sizesClass}
            errors={props.errorsSid}
            nameApplication={nameApplication}
            subject={props.subject}
            defaultEmail={props.defaultEmail}
            coraSubjectSid={props.coraSubjectSid}
            initialCoraSubjectSid={props.initialCoraSubjectSid}
            certificateFiles={certificateFiles}
            fiscalCode={values.fiscalCode}
            setCertificateFiles={setCertificateFiles}
            setIsCertSidOpened={setIsCertSidOpened}
            setErrorCertificates={(errorCertificates) =>
              props.setErrorsSid({ ...props.errorsSid, errorCertificates })
            }
            checkSid={checkSid}
            handleChangeSid={handleChangeSid}
            handleChangeSidValues={handleChangeSidValues}
            saveSubject={(newCoraSubjectSid, files) => {
              props.setInitialCoraSubjectSid(newCoraSubjectSid)
              handleFormSubmit(newCoraSubjectSid, files, (subject) =>
                onSuccess(subject, props.onOtherSaveAction)
              )
            }}
          />
        </div>
      </div>
      <div className="form-row mt-2 row align-item-center">
        <div className="d-flex justify-content-between">
          <div className="d-flex align-items-center">
            <button
              type="button"
              disabled={props.disabled}
              className="btn btn-outline-primary me-4"
              onClick={() => props.close(true)}>
              <i className="star-icon-cancel star-icon me-2" />
              {labels.CANCEL}
            </button>
            {props.paginationComponent && (
              <div className="d-flex justify-content-end">{props.paginationComponent}</div>
            )}
            {window.innerWidth > Constants.BREAKPOINT_LG && (
              <div className="ms-4">
                <b className="me-2">*</b>
                <span>{labels.REQUIRED_FIELD_FOR_FORM_COMPLETION}</span>
              </div>
            )}
          </div>
          <div className="d-flex align-items-center">
            {values?.id && (diff < Constants.ARCHIVE_UNDO_TIMEOUT || !values?.archived) && (
              <button
                disabled={
                  props.loading || (diff >= Constants.ARCHIVE_UNDO_TIMEOUT && values?.archived)
                }
                className="btn btn-outline-primary me-2"
                onClick={() => handleArchive()}>
                <i className="star-icon-attachment star-icon me-2" />
                {values?.archived &&
                values?.lastArchivedDate &&
                diff < Constants.ARCHIVE_UNDO_TIMEOUT
                  ? labels.UNARCHIVE
                  : labels.ARCHIVE}
              </button>
            )}
            <button
              type="submit"
              disabled={props.disabled || props.loading || values.archived}
              className="btn btn-primary ml-2"
              onClick={() =>
                handleFormSubmit(null, null, (subject) => onSuccess(subject, props.onSaveClick))
              }>
              <i className="star-icon-floppy-disk star-icon me-2" />
              {labels.SAVE}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
